import { Component } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { NuclearObservable } from '../nuclear-observable'
import { BaseComponent } from '../components/base.component'
import {User, Account, BillingAccountWithBillingInfo, Country, State} from '../types'
import {geoGetters} from '../stores/geo-store'
import {customerGetters} from '../stores/customer-store'
import { UserActions } from '../actions/user-actions'
import { AccountActions } from '../actions/account-actions'
import { GeoActions } from '../actions/geo-actions'

@Component({
  selector: 'payment-info-page',
  template: `
    <header-left-nav-layout>
      <div sidenav>
        <customers-nav></customers-nav>
      </div>
      <div class="container-fluid" main>
        <div class="row top-buffer bottom-buffer" style="margin-left: 0; margin-right: 0;">
          <div class="col-sm-6">
            <div class="loader" *ngIf="!billingAccountLoaded">Loading...</div>
            <div *ngIf="billingAccountLoaded">
              <h4>Payment & Invoice Info</h4>

              <div class="row well">
                <div class="col-sm-12">
                  <div class="row">
                    <div class="col-sm-6">
                      <label>Payment Method</label>
                      <div>{{billingAccount?.method === 'paypal' ? 'Subscribed with PayPal' : numberPlaceholder()}}</div>
                    </div>
                    <div class="col-sm-6">
                      <div class="float-right text-right">
                        <a class="link" (click)="toggleEditBillingInfo(!editing)">{{editing ? 'Cancel editing' : 'Update payment info'}}</a>
                      </div>
                    </div>
                  </div>

                  <hr class="small-top-buffer">
                  <div class="row">
                    <div class="col-sm-12">
                      <label for="email">Email</label>
                      <div>{{billingAccount?.email}}</div>
                    </div>
                  </div>

                  <ng-template [ngIf]="billingAccount?.method === 'credit_card'">

                    <hr class="small-top-buffer">
                    <div class="row">
                      <div class="col-sm-12">
                        <label for="bizName">Company Name</label>
                        <div>{{billingAccount?.billingInfo.company}}</div>
                      </div>
                    </div>

                    <hr class="small-top-buffer">
                    <div class="row">
                      <div class="col-sm-12">
                        <label for="address">Billing Address</label>
                      </div>
                      <div class="col-sm-12">
                        {{billingAccount?.billingInfo.address1}} {{billingAccount?.billingInfo.address2}}
                      </div>

                      <div class="col-sm-12">
                        {{billingAccount?.billingInfo.city}}, {{billingAccount?.billingInfo.state}} {{billingAccount?.billingInfo.zip}} {{billingAccount?.billingInfo.country}}
                      </div>
                    </div>

                  </ng-template>

                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-6">
            <div *ngIf="billingAccountLoaded" [hidden]="!editing">
              <payment-info-form
                [currentUser]="currentUser"
                [account]="account"
                [billingAccount]="billingAccount"
                [countries]="countries"
                [states]="states"
                (onSubmitSuccess)="updateWithEdits()">
              </payment-info-form>
            </div>
          </div>
        </div>
      </div>
    </header-left-nav-layout>
  `
})
export class PaymentInfoPage extends BaseComponent {
  currentUser: User;
  account: Account;
  billingAccount: BillingAccountWithBillingInfo;
  countries: Country[] = [];
  states: State[] = [];
  billingAccountLoaded: boolean = false;
  editing: boolean = false;
  returnUrl: string

  constructor(UserActions: UserActions,
              private AccountActions: AccountActions,
              GeoActions: GeoActions,
              private router: Router,
              private route: ActivatedRoute) {
    super()

    const routeObs = this.route.queryParams.subscribe(queryParams => {
      this.editing = queryParams['editing']
      this.returnUrl = queryParams['returnUrl']
    })

    // Note: Layout also loads the user and account but it might not be loaded by the this constructor runs

    // Fetch from store in case already loaded
    this.observeUser(u => this.currentUser = u)
    this.observeAccount(a => this.account = a)
    const billingObs = NuclearObservable.from<BillingAccountWithBillingInfo>(customerGetters.billingAccount, { toJS: true }).subscribe(ba => this.billingAccount = ba)
    const countriesObs = NuclearObservable.from<Country[]>(geoGetters.countries, { toJS: true }).subscribe(countries => this.countries = countries)

    UserActions.loadUser()
    AccountActions.loadAccount()
    GeoActions.loadCountries()

    // Load the recurly account
    AccountActions.loadBillingAccount().then(() => {
      this.billingAccountLoaded = true
      const selectedCountry = this.billingAccount?.billingInfo?.country || 'US'
      GeoActions.loadStatesForCountry(selectedCountry).then(() => {
        this.states = NuclearObservable.currentValue<State[]>(geoGetters.states(selectedCountry))
      })
    })

    this.observers.push(billingObs, countriesObs, routeObs)
  }

  numberPlaceholder(): string {
    return this.billingAccount && this.billingAccount.billingInfo && this.billingAccount.billingInfo.maskedCc.replace(/X/g, '•').replace(/-/g, ' ')
  }

  toggleEditBillingInfo(editing: boolean): void {
    const { returnUrl } = this
    if (editing) {
      this.router.navigate([], { relativeTo: this.route, queryParams: { editing, returnUrl } })
    } else {
      this.router.navigate([], { relativeTo: this.route, queryParams: { returnUrl } })
    }
  }

  ngOnDestroy(): void {
    this.clearObservers()
  }

  updateWithEdits(): void {
    this.AccountActions.loadBillingAccount()
    this.toggleEditBillingInfo(false)
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl)
    }
  }
}
